import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { RadioStation } from '@/mocks/radios';
import { useAudioPlayer } from '@/context/AudioContext';
import { radioProfiles, getRadioTeam } from '@/mocks/radioProfile';
import { getStationLives, getStationPhotos, getStationVideos } from '@/mocks/radioContent';
import { fetchCentovaMetadata } from '@/services/centovaMetadata';
import RadioFeedSection from './RadioFeedSection';
import RadioEventosSection from './RadioEventosSection';
import RadioConcursosSection from './RadioConcursosSection';
import SeccionComunidad from './SeccionComunidad';
import SeccionAcerca from './SeccionAcerca';
import ShareModal from '@/components/feature/ShareModal';
import LikeButton from '@/components/feature/LikeButton';

interface NowPlayingMeta {
  song: string | null;
  artist: string | null;
  title: string | null;
  listeners: number;
  status: 'online' | 'offline' | 'error';
  fetched_at: string;
}

const TABS = [
  { id: 'feed', label: 'Feed', icon: 'ri-home-5-line' },
  { id: 'schedule', label: 'Programación', icon: 'ri-calendar-2-line' },
  { id: 'content', label: 'Contenido', icon: 'ri-gallery-line' },
  { id: 'events', label: 'Eventos', icon: 'ri-calendar-event-line' },
  { id: 'contests', label: 'Concursos', icon: 'ri-trophy-line' },
  { id: 'community', label: 'Comunidad', icon: 'ri-group-line' },
  { id: 'about', label: 'Acerca', icon: 'ri-information-line' },
];

const INSTITUTIONAL_ACCESS = [
  { label: 'Noticias RFW', icon: 'ri-newspaper-line', route: null, soon: true },
  { label: 'Entrevistas', icon: 'ri-mic-line', route: null, soon: true },
  { label: 'Programas', icon: 'ri-calendar-2-line', route: '/radio/radiofrecuenciaweb', soon: false },
  { label: 'Radios', icon: 'ri-star-line', route: '/live', soon: false },
  { label: 'Rankings', icon: 'ri-trophy-line', route: '/rankings', soon: false },
  { label: 'Eventos', icon: 'ri-calendar-event-line', route: '/eventos', soon: false },
  { label: 'App', icon: 'ri-smartphone-line', route: '/download-app', soon: false },
  { label: 'Registrar', icon: 'ri-add-circle-line', route: '/register-radio', soon: false },
];

interface OfficialMobileProfileProps {
  station: RadioStation;
}

export default function OfficialMobileProfile({ station }: OfficialMobileProfileProps) {
  const { play, isPlaying, currentStation } = useAudioPlayer();
  const [activeTab, setActiveTab] = useState('feed');
  const [following, setFollowing] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [nowPlaying, setNowPlaying] = useState<NowPlayingMeta | null>(null);

  const isPlayingThis = isPlaying && currentStation?.id === station.id;
  const profile = radioProfiles[station.id];
  const team = getRadioTeam(station.id);
  const followers = 12500 + station.listeners * 2;
  const programsCount = profile?.schedule?.length || 4;

  /* ── Polling metadata ── */
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    async function poll() {
      try {
        const meta = await fetchCentovaMetadata();
        if (!cancelled) setNowPlaying(meta);
      } catch { /* silencioso */ }
      if (!cancelled) timer = setTimeout(poll, 15000);
    }
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  const nowPlayingLabel = useMemo(() => {
    if (!nowPlaying || !nowPlaying.title) return null;
    if (nowPlaying.artist && nowPlaying.artist.trim() !== 'Unknown' && nowPlaying.artist.trim() !== '') {
      return { title: nowPlaying.title.trim(), artist: nowPlaying.artist.trim() };
    }
    return { title: nowPlaying.title.trim(), artist: null };
  }, [nowPlaying]);

  const formatNumber = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  return (
    <div className="md:hidden min-h-screen bg-background-50 pb-24">
      {/* ── HERO MOBILE OFICIAL ── */}
      <div className="relative w-full bg-gradient-to-b from-background-950 via-background-950 to-background-50 pt-6 pb-8 px-4 overflow-hidden">
        {/* Background glow */}
        <div className="absolute top-0 right-0 w-48 h-48 bg-accent-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4"></div>
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-primary-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4"></div>

        {/* Back button */}
        <Link
          to="/app"
          className="relative z-10 inline-flex items-center gap-1 text-background-50/70 text-xs font-medium mb-4 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-sm"></i>
          Volver
        </Link>

        {/* Badge + Logo */}
        <div className="relative z-10 flex items-center gap-3 mb-4">
          <div className="w-16 h-16 rounded-2xl overflow-hidden bg-background-50/10 backdrop-blur-sm border border-background-50/20 shrink-0">
            <img src={station.logoUrl} alt={station.name} className="w-full h-full object-cover" />
          </div>
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-500/20 border border-accent-500/30 text-accent-300 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap mb-1.5">
              <span className="w-1.5 h-1.5 bg-accent-500 rounded-full animate-pulse"></span>
              RADIO OFICIAL
            </span>
            <h1 className="text-xl font-extrabold text-background-50 leading-tight">{station.name}</h1>
          </div>
        </div>

        {/* Title + Description */}
        <div className="relative z-10 mb-4">
          <h2 className="text-base font-bold text-background-50/90 mb-2">La Voz Oficial de RadioFrecuenciaWeb</h2>
          <p className="text-xs text-background-50/60 leading-relaxed">
            La voz oficial de RadioFrecuenciaWeb. Noticias, entrevistas, programas especiales, promoción de radios afiliadas y comunidad.
          </p>
        </div>

        {/* Stats */}
        <div className="relative z-10 flex items-center gap-4 text-xs text-background-50/70 mb-5">
          <span><strong className="text-background-50">{formatNumber(followers)}</strong> seguidores</span>
          <span><strong className="text-background-50">{formatNumber(nowPlaying?.listeners || 0)}</strong> oyentes</span>
          <span><strong className="text-background-50">{programsCount}</strong> programas</span>
        </div>

        {/* Action Buttons */}
        <div className="relative z-10 flex flex-wrap items-center gap-2 mb-5">
          <button
            onClick={() => play(station)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold cursor-pointer transition-all whitespace-nowrap ${
              isPlayingThis
                ? 'bg-accent-500 text-background-50 dark:text-foreground-950'
                : 'bg-background-50 text-foreground-950'
            }`}
          >
            <i className={`text-sm ${isPlayingThis ? 'ri-pause-fill' : 'ri-play-fill'}`}></i>
            {isPlayingThis ? 'Pausar' : 'Escuchar'}
          </button>
          <button
            onClick={() => setFollowing(!following)}
            className={`flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
              following
                ? 'bg-background-50/10 text-background-50 border border-background-50/30'
                : 'bg-accent-500 text-background-50 dark:text-foreground-950'
            }`}
          >
            <i className={`text-sm ${following ? 'ri-user-follow-fill' : 'ri-user-follow-line'}`}></i>
            {following ? 'Siguiendo' : 'Seguir'}
          </button>
          <LikeButton entityId={station.id} entityType="radio" initialLikes={0} initialLiked={false} size="sm" />
          <button
            onClick={() => setShowShareModal(true)}
            className="w-9 h-9 rounded-xl bg-background-50/10 border border-background-50/30 flex items-center justify-center cursor-pointer text-background-50"
          >
            <i className="ri-share-forward-line text-sm"></i>
          </button>
        </div>

        {/* ── Al Aire Ahora Panel ── */}
        <div className="relative z-10 bg-background-50/95 backdrop-blur-sm rounded-xl p-4 border border-background-200/70">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 bg-accent-500 rounded-full animate-pulse"></span>
            <span className="text-xs font-bold text-foreground-950 uppercase tracking-wide">Al Aire Ahora</span>
          </div>
          {nowPlayingLabel ? (
            <>
              <p className="text-sm font-semibold text-foreground-950 truncate">{nowPlayingLabel.title}</p>
              {nowPlayingLabel.artist && (
                <p className="text-xs text-foreground-500 mt-0.5 truncate">{nowPlayingLabel.artist}</p>
              )}
              <div className="flex items-center gap-2 mt-2">
                <span className="w-1.5 h-1.5 bg-accent-500 rounded-full animate-pulse"></span>
                <span className="text-[10px] font-semibold text-accent-600">EN VIVO</span>
              </div>
            </>
          ) : (
            <p className="text-xs text-foreground-500">Transmisión oficial en vivo</p>
          )}
        </div>
      </div>

      {/* ── ACCESOS INSTITUCIONALES ── */}
      <div className="px-4 py-5">
        <h3 className="text-[10px] font-bold text-foreground-400 uppercase tracking-[0.2em] mb-3">Explora</h3>
        <div className="grid grid-cols-2 gap-2">
          {INSTITUTIONAL_ACCESS.map((item) => {
            const card = (
              <div className="flex items-center gap-2.5 p-3 rounded-xl bg-background-100 border border-background-200/70 hover:bg-background-200/50 transition-colors cursor-pointer relative">
                <div className="w-8 h-8 rounded-lg bg-background-50 flex items-center justify-center shadow-sm shrink-0">
                  <i className={`${item.icon} text-sm text-foreground-700`}></i>
                </div>
                <span className="text-xs font-semibold text-foreground-800">{item.label}</span>
                {item.soon && (
                  <span className="absolute top-1.5 right-2 text-[9px] font-medium text-foreground-400 italic">
                    Pronto
                  </span>
                )}
              </div>
            );
            return item.route && !item.soon ? (
              <Link key={item.label} to={item.route}>{card}</Link>
            ) : (
              <div key={item.label}>{card}</div>
            );
          })}
        </div>
      </div>

      {/* ── TABS ── */}
      <div className="sticky top-0 z-20 bg-background-50 border-b border-background-200/70">
        <div className="flex items-center gap-0 overflow-x-auto scrollbar-hide px-2">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold whitespace-nowrap border-b-2 transition-all cursor-pointer shrink-0 ${isActive ? 'border-primary-500 text-primary-600' : 'border-transparent text-foreground-500'}`}
              >
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── TAB CONTENT ── */}
      <div className="px-4 py-4">
        {activeTab === 'feed' && <RadioFeedSection station={station} />}
        {activeTab === 'schedule' && <MobileScheduleTab station={station} />}
        {activeTab === 'content' && <MobileContentTab station={station} />}
        {activeTab === 'events' && <RadioEventosSection station={station} />}
        {activeTab === 'contests' && <RadioConcursosSection station={station} />}
        {activeTab === 'community' && <SeccionComunidad station={station} />}
        {activeTab === 'about' && <SeccionAcerca station={station} />}
      </div>

      <ShareModal
        entityName={station.name}
        entitySubtitle="RFW Oficial"
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
    </div>
  );
}

/* ─── SUB-TABS ─── */

type ContentSubTab = 'photos' | 'videos' | 'lives';

function MobileContentTab({ station }: { station: RadioStation }) {
  const [subTab, setSubTab] = useState<ContentSubTab>('photos');
  const photos = getStationPhotos(station.id);
  const videos = getStationVideos(station.id);
  const lives = getStationLives(station.id);

  const subTabs: { key: ContentSubTab; label: string; icon: string }[] = [
    { key: 'photos', label: 'Fotos', icon: 'ri-image-line' },
    { key: 'videos', label: 'Videos', icon: 'ri-video-line' },
    { key: 'lives', label: 'Lives', icon: 'ri-live-line' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1 bg-background-100 rounded-full p-0.5 overflow-x-auto scrollbar-hide">
        {subTabs.map((st) => {
          const isActive = subTab === st.key;
          return (
            <button
              key={st.key}
              onClick={() => setSubTab(st.key)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all whitespace-nowrap shrink-0 ${isActive ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500'}`}
            >
              <i className={`${st.icon} text-xs`}></i>
              {st.label}
            </button>
          );
        })}
      </div>

      {subTab === 'photos' && (
        <div className="py-12 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
            <i className="ri-image-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-sm text-foreground-500">Galería próximamente</p>
        </div>
      )}
      {subTab === 'videos' && (
        <div className="py-12 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
            <i className="ri-video-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-sm text-foreground-500">Videos próximamente</p>
        </div>
      )}
      {subTab === 'lives' && (
        <div className="py-12 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
            <i className="ri-live-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-sm text-foreground-500">Lives próximamente</p>
        </div>
      )}
    </div>
  );
}

function MobileScheduleTab({ station }: { station: RadioStation }) {
  const profile = radioProfiles[station.id];

  if (!profile || profile.schedule.length === 0) {
    return (
      <div className="py-12 text-center">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
          <i className="ri-calendar-2-line text-2xl text-foreground-400"></i>
        </div>
        <p className="text-sm text-foreground-500">Programación no disponible</p>
      </div>
    );
  }

  return (
    <section className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-background-200/70">
        <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
          <i className="ri-time-line text-accent-600 text-sm"></i>
        </div>
        <div>
          <h3 className="text-sm font-bold text-foreground-950">Programación</h3>
          <p className="text-[11px] text-foreground-500">{profile.schedule.length} programas</p>
        </div>
      </div>
      <div className="divide-y divide-background-200/70">
        {profile.schedule.map((entry, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3 hover:bg-background-100/50 transition-colors">
            <div className="flex flex-col items-start min-w-[80px] shrink-0">
              <span className="text-[11px] font-semibold text-foreground-600 whitespace-nowrap">{entry.day}</span>
              <span className="text-xs font-bold text-foreground-950 whitespace-nowrap">{entry.timeRange}</span>
            </div>
            <div className="w-px self-stretch bg-background-200/70"></div>
            <div className="flex-1 min-w-0">
              <h4 className="text-xs font-bold text-foreground-950">{entry.program}</h4>
              <p className="text-[11px] text-foreground-500">con {entry.host}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}